import React from 'react';
import { View, StyleSheet, Platform, TouchableOpacity } from 'react-native';
import { Surface, Text, useTheme } from 'react-native-paper';
import { MaterialCommunityIcons } from '@expo/vector-icons';

export interface InfoBoxProps {
  /**
   * Type of info box (changes icon and color)
   */
  variant?: 'info' | 'tip' | 'warning' | 'success';
  
  /**
   * Title of the info box
   */
  title?: string;
  
  /**
   * Main content text
   */
  message: string;
  
  /**
   * Custom icon name (MaterialCommunityIcons)
   */
  icon?: string;
  
  /**
   * Whether the info box can be dismissed
   */
  dismissible?: boolean;
  
  /**
   * Callback when dismiss button is pressed
   */
  onDismiss?: () => void;
  
  /**
   * Test ID for testing
   */
  testID?: string;
}

/**
 * InfoBox component displays an informational message with an icon.
 * 
 * Usage:
 * ```tsx
 * <InfoBox
 *   variant="tip"
 *   title="Dica"
 *   message="Convide os motoristas para preencherem os próprios dados!"
 * />
 * 
 * <InfoBox
 *   variant="warning"
 *   message="Não tem licenças disponíveis. Compre mais licenças."
 * />
 * 
 * <InfoBox
 *   variant="tip"
 *   title="Multi-Frota Disponível"
 *   message="Pode adicionar mais empresas para gerir múltiplas frotas independentes."
 *   dismissible
 *   onDismiss={() => setDismissed(true)}
 * />
 * ```
 */
export const InfoBox: React.FC<InfoBoxProps> = ({
  variant = 'info',
  title,
  message,
  icon,
  dismissible = false,
  onDismiss,
  testID = `info-box-${variant}`,
}) => {
  const theme = useTheme();

  // Determine icon and colors based on variant
  const getVariantConfig = () => {
    switch (variant) {
      case 'tip':
        return {
          icon: icon || 'lightbulb-on-outline',
          iconColor: '#FFA000', // Amber
          backgroundColor: 'rgba(255, 160, 0, 0.1)',
          borderColor: 'rgba(255, 160, 0, 0.3)',
        };
      case 'warning':
        return {
          icon: icon || 'alert-circle-outline',
          iconColor: '#F57C00', // Orange
          backgroundColor: 'rgba(245, 124, 0, 0.1)',
          borderColor: 'rgba(245, 124, 0, 0.3)',
        };
      case 'success':
        return {
          icon: icon || 'check-circle-outline',
          iconColor: '#388E3C', // Green
          backgroundColor: 'rgba(56, 142, 60, 0.1)',
          borderColor: 'rgba(56, 142, 60, 0.3)',
        };
      case 'info':
      default:
        return {
          icon: icon || 'information-outline',
          iconColor: theme.colors.primary,
          backgroundColor: `${theme.colors.primary}15`, // 15 = ~9% opacity in hex
          borderColor: `${theme.colors.primary}30`, // 30 = ~19% opacity in hex
        };
    }
  };

  const config = getVariantConfig();

  return (
    <Surface
      style={[
        styles.container,
        {
          backgroundColor: config.backgroundColor,
          borderColor: config.borderColor,
        },
      ]}
      testID={testID}
    >
      <View style={styles.iconContainer}>
        <MaterialCommunityIcons
          name={config.icon as any}
          size={24}
          color={config.iconColor}
        />
      </View>
      
      <View style={styles.content}>
        {title && (
          <Text variant="titleSmall" style={[styles.title, { color: config.iconColor }]}>
            {title}
          </Text>
        )}
        <Text variant="bodyMedium" style={styles.message}>
          {message}
        </Text>
      </View>
      
      {dismissible && (
        <TouchableOpacity
          onPress={onDismiss}
          style={styles.dismissButton}
          testID={`${testID}-dismiss`}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        >
          <MaterialCommunityIcons
            name="close"
            size={20}
            color={config.iconColor}
          />
        </TouchableOpacity>
      )}
    </Surface>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    marginVertical: 8,
  },
  iconContainer: {
    marginRight: 12,
    marginTop: 2,
  },
  content: {
    flex: 1,
  },
  title: {
    fontWeight: '600',
    marginBottom: 4,
  },
  message: {
    lineHeight: 20,
  },
  dismissButton: {
    marginLeft: 8,
    padding: 2,
    alignSelf: 'flex-start',
  },
});

